import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ComposedChart, Cell, PieChart, Pie, Area, ReferenceLine
} from 'recharts';
import { api } from '../../api';
import axios from 'axios';
import { isSameDay } from 'date-fns';

// Import skeleton loaders for progressive loading
import {
  MetricCardSkeleton,
  ChartSkeleton,
  RankingPanelSkeleton,
  DashboardSkeleton
} from './SkeletonLoader';

// Import new components
import DashboardHeader from './DashboardHeader';
import MetricCard from './MetricCard';
import TabNavigation from './TabNavigation';
import ChartContainer from './ChartContainer';
import HourlyMixedComparisonChart from './HourlyMixedComparisonChart';
import WeeklyComparisonChart from './WeeklyComparisonChart';
import WeeklyMixedComparisonChart from './WeeklyMixedComparisonChart';
import { 
  BarChartIcon, LineChartIcon, TrendUpIcon, StarIcon, 
  ClockIcon, UsersIcon, FireIcon, PieChartIcon, CalendarIcon, BoltIcon 
} from './DashboardIcons';
import DailyInteractionsChart from './DailyInteractionsChart';
import MonthlyHighlightCard from './MonthlyHighlightCard';
import TopBotsTable from './TopBotsTable';
import InteractionRangeAnalysis from './InteractionRangeAnalysis';
import BotAlertsPanel from './BotAlertsPanel';
import TopBotsRankingPanel from './TopBotsRankingPanel';
import FDRankingPanel from './FDRankingPanel';
import PartenonTab from './PartenonTab';

// Interface definitions
interface HourlyDataItem {
  hora: number;
  total_interacoes: number;
}

interface WeeklyDataItem {
  data: string;
  dia_semana: string;
  total_interacoes: number;
}

interface BotClientItem {
  botclient: string;
  total_interacoes: number;
}

interface TotalInteractionsResponse {
  total_interacoes: number;
}

// Nova interface para dados por minuto
interface MinuteDataItem {
  minuto: number;
  hora: number;
  minuto_hora: number;
  interacoes_hoje: number;
  interacoes_ontem: number;
  interacoes_semana_anterior: number;
}

// Adicionar as interfaces para os novos dados
interface TopBotData {
  bot_id: string;
  bot_name: string;
  total_interactions: number;
}

interface MonthlyData {
  month: string;
  total_interactions: string;
}

interface DailyData {
  day: string;
  total_interactions: string;
}

interface Aggregated10MinData {
  interval: string;
  hoje: number;
  ontem: number;
  semana_anterior: number;
}

interface MinuteData {
  minute: string;
  count: string;
}

// Adicionar interface para os dados do mês corrente
interface CurrentMonthData {
  total_interacoes: number;
  valor_monetario: number;
  month_start: string;
  month_end: string;
  current_timestamp: string;
}

// Constants
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe', '#00C49F', '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57'];

// Adicionar componente Tooltip personalizado
const InfoTooltip: React.FC<{ content: string }> = ({ content }) => (
  <div className="group relative inline-block ml-1">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 hover:text-gray-200 cursor-help transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <div className="absolute z-10 w-60 p-2 -mt-1 ml-3 text-xs rounded-md bg-gray-800 text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg border border-gray-700">
      {content}
    </div>
  </div>
);

// Componente para Badge
const Badge: React.FC<{ value: number }> = ({ value }) => (
  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
    {value}
  </span>
);

const DashboardIntegrado: React.FC = () => {
  // Sistema de debug condicional para performance
  const DEBUG = process.env.NODE_ENV === 'development' && false;
  const debug = (...args: any[]) => {
    if (DEBUG) console.log('[Dashboard Debug]', ...args);
  };
  debug('🎆 DashboardIntegrado COMPONENT MOUNTED/RENDERED');
  
  const [tab, setTab] = useState('hourly');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Flag para controlar se meta foi atingida
  const [goalTriggered, setGoalTriggered] = useState(false);
  
  // Emojis celebrativos
  const celebrationEmojis = ["🚀", "🎯", "🎉", "🏆", "✨", "💯", "🔥", "⚡", "🌟", "💪", "🥇"];
  const [currentEmojis, setCurrentEmojis] = useState<string[]>(["🚀", "🎯"]);
  
  // Variável para controlar se deve mostrar o efeito de confete
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Interaction data
  const [totalInteractions, setTotalInteractions] = useState<number>(0);
  const [todayTotalUntilNow, setTodayTotalUntilNow] = useState<number>(0);
  const [yesterdayTotalInteractions, setYesterdayTotalInteractions] = useState<number>(0);
  const [yesterdayTotalUntilNow, setYesterdayTotalUntilNow] = useState<number>(0);
  const [lastWeekTotalInteractions, setLastWeekTotalInteractions] = useState<number>(0);
  const [lastWeekTotalUntilNow, setLastWeekTotalUntilNow] = useState<number>(0);
  const [yesterdayPercentage, setYesterdayPercentage] = useState<number>(0);
  const [lastWeekPercentage, setLastWeekPercentage] = useState<number>(0);
  const [hourlyData, setHourlyData] = useState<any[]>([]);
  const [minuteData, setMinuteData] = useState<MinuteDataItem[]>([]);
  const [minuteDataFiltered, setMinuteDataFiltered] = useState<MinuteDataItem[]>([]);
  const [minuteViewRange, setMinuteViewRange] = useState<[number, number]>([0, 1439]); // [start, end] in minutes
  const [weeklyData, setWeeklyData] = useState<any[]>([]);
  const [topBots, setTopBots] = useState<any[]>([]);
  const [topBotClients, setTopBotClients] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'hourly' | 'weekly' | 'monthly' | 'evolution'>('hourly');
  const [todayTotalInteractions, setTodayTotalInteractions] = useState<number>(0);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [dailyData, setDailyData] = useState<DailyData[]>([]);
  const [aggregated10MinData, setAggregated10MinData] = useState<Aggregated10MinData[]>([]);
  const [hourlyComparisonMixedData, setHourlyComparisonMixedData] = useState<any[]>([]);
  const [hourlyComparisonMixedLoading, setHourlyComparisonMixedLoading] = useState<boolean>(true);
  const [topWeeklyBots, setTopWeeklyBots] = useState<any[]>([]);
  const [totalWeeklyInteractions, setTotalWeeklyInteractions] = useState<number>(0);
  const [weeklyInteractionsPerDay, setWeeklyInteractionsPerDay] = useState<any[]>([]);
  const [topMonthlyBots, setTopMonthlyBots] = useState<any[]>([]);
  const [totalMonthlyInteractions, setTotalMonthlyInteractions] = useState<number>(0);
  const [monthlyInteractionsPerDay, setMonthlyInteractionsPerDay] = useState<any[]>([]);
  
  // Variáveis para horário atual
  const [currentHour, setCurrentHour] = useState<number>(new Date().getHours());
  const [currentMinute, setCurrentMinute] = useState<number>(new Date().getMinutes());
  const [currentDayOfWeek, setCurrentDayOfWeek] = useState<number>(new Date().getDay()); // 0 = Domingo, 6 = Sábado
  
  // State para rastrear se estamos mostrando dados do dia atual ou históricos
  const [isShowingCurrentData, setIsShowingCurrentData] = useState<boolean>(true);
  const [mostRecentDate, setMostRecentDate] = useState<Date | null>(null);
  
  // Adicione esse novo estado para os dados do mês corrente
  const [currentMonthData, setCurrentMonthData] = useState<CurrentMonthData>({
    total_interacoes: 0,
    valor_monetario: 0,
    month_start: '',
    month_end: '',
    current_timestamp: ''
  });
  const [currentMonthLoading, setCurrentMonthLoading] = useState<boolean>(true);
  
  // Função para escolher emojis aleatórios
  const pickRandomEmojis = () => {
    const shuffled = [...celebrationEmojis].sort(() => 0.5 - Math.random());
    setCurrentEmojis(shuffled.slice(0, 2));
  };
  
  // Função para disparar o efeito de confete
  const triggerConfetti = () => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 5000);
  };
  
  // Format data for display
  const formatHourlyData = (data: any[]) => {
    debug('Formatting hourly data:', data);
    
    if (!data || !Array.isArray(data)) {
      console.warn('Invalid hourly data format:', data);
      return [];
    }
    
    return data.map(item => {
      // Handle different possible data structures
      const hour = item.hora !== undefined ? item.hora : 
                  (item.hour !== undefined ? item.hour : 0);
      
      const interactions = item.total_interacoes !== undefined ? item.total_interacoes : 
                         (item.hoje !== undefined ? item.hoje : 
                         (item.total !== undefined ? item.total : 0));
      
      return {
        hora: String(hour).padStart(2, '0'),
        interacoes: interactions,
        hoje: interactions,
        anterior: item.anterior || item.ontem || 0,
        semana_anterior: item.semana_anterior || 0
      };
    });
  };
  
  const formatWeeklyData = (data: WeeklyDataItem[]) => {
    return data.map(item => {
      const date = new Date(item.data);
      const formattedDate = `${item.dia_semana.trim()} ${date.getDate()}/${date.getMonth() + 1}`;
      return {
        dia: formattedDate,
        interacoes: item.total_interacoes
      };
    });
  };
  
  // Fetch data from API
  useEffect(() => {
    debug('🟡 useEffect TRIGGERED - Initial data fetch');
    debug('Dependencies: [] (empty - runs once on mount)');
    
    // Initial data fetch
    fetchDashboardData();
    
    // Check and refresh at 6:00 AM daily
    const checkAndRefresh = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      
      // Refresh at 6:00 AM (with 5 minute margin)
      if (hours === 6 && minutes >= 0 && minutes < 5) {
        debug('🔄 Auto-refresh scheduled at 6:00 AM');
        fetchDashboardData();
      }
    };
    
    // Check every 6 hours if it's time to refresh
    const interval = setInterval(checkAndRefresh, 6 * 60 * 60 * 1000);
    
    return () => {
      debug('🔴 useEffect CLEANUP - Clearing interval');
      clearInterval(interval);
    };
  }, []);
  
  // Filtrar dados por minuto sempre que minuteData ou minuteViewRange mudar
  useEffect(() => {
    if (minuteData.length > 0) {
      const [start, end] = minuteViewRange;
      setMinuteDataFiltered(minuteData.filter(item => item.minuto >= start && item.minuto <= end));
    }
  }, [minuteData, minuteViewRange]);

  // Função para buscar dados adicionais não inclusos no endpoint consolidado
  const fetchAdditionalData = useCallback(async () => {
    // Buscar top bots semanais e mensais em paralelo
    try {
      const [topWeeklyResponse, topMonthlyResponse] = await Promise.all([
        api.getTopBotsWeekly().catch(() => null),
        api.getTopBotsMonthly().catch(() => null)
      ]);

      if (topWeeklyResponse?.data) {
        setTopWeeklyBots(topWeeklyResponse.data);
      } else if (Array.isArray(topWeeklyResponse)) {
        setTopWeeklyBots(topWeeklyResponse);
      }

      if (topMonthlyResponse?.data) {
        setTopMonthlyBots(topMonthlyResponse.data);
      } else if (Array.isArray(topMonthlyResponse)) {
        setTopMonthlyBots(topMonthlyResponse);
      }
    } catch (error) {
      // Silenciar erros - dados adicionais são opcionais
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    debug('=== INÍCIO FETCH DASHBOARD DATA ===');

    try {
      setLoading(true);
      setError(null);

      // ============================================================
      // OTIMIZADO: Usar endpoint consolidado v2 (2s vs 40s)
      // Retorna todos os dados principais em uma única requisição
      // ============================================================
      const consolidatedData = await api.getInitialDashboardData();

      if (consolidatedData?.success) {
        // Processar dados do servidor
        if (consolidatedData.serverTime) {
          setCurrentHour(consolidatedData.serverTime.current_hour || 0);
          setCurrentMinute(consolidatedData.serverTime.current_minute || 0);
          setCurrentDayOfWeek(consolidatedData.serverTime.day_of_week || 0);
        }

        // Processar totais
        if (consolidatedData.totals) {
          setTotalInteractions(consolidatedData.totals.today || 0);
          setYesterdayTotalInteractions(consolidatedData.totals.yesterday || 0);
          setLastWeekTotalInteractions(consolidatedData.totals.lastWeek || 0);
          setTotalWeeklyInteractions(consolidatedData.totals.weeklyTotal || 0);
          setTotalMonthlyInteractions(consolidatedData.totals.monthlyTotal || 0);

          // Atualizar dados do mês corrente
          setCurrentMonthData({
            total_interacoes: consolidatedData.totals.monthlyTotal || 0,
            valor_monetario: consolidatedData.totals.monthlyValue || 0,
            month_start: '',
            month_end: '',
            current_timestamp: ''
          });
        }

        // Processar comparações
        if (consolidatedData.comparisons) {
          setTodayTotalUntilNow(consolidatedData.comparisons.todayUntilHour || 0);
          setYesterdayTotalUntilNow(consolidatedData.comparisons.yesterdayUntilHour || 0);
          setLastWeekTotalUntilNow(consolidatedData.comparisons.lastWeekUntilHour || 0);
          setYesterdayPercentage(consolidatedData.comparisons.dailyDeltaPercentage || 0);
          setLastWeekPercentage(consolidatedData.comparisons.weeklyDeltaPercentage || 0);
        }

        // Processar gráficos
        if (consolidatedData.charts?.hourlyMixed) {
          setHourlyComparisonMixedData(consolidatedData.charts.hourlyMixed);
          // Converter para formato hourlyData
          setHourlyData(consolidatedData.charts.hourlyMixed.map((h: any) => ({
            hora: h.hora,
            total_interacoes: h.hoje
          })));
        }

        if (consolidatedData.charts?.weeklyDaily) {
          setWeeklyInteractionsPerDay(consolidatedData.charts.weeklyDaily);
        }

        // Processar dados mensais
        if (consolidatedData.charts?.monthlyDaily) {
          setMonthlyInteractionsPerDay(consolidatedData.charts.monthlyDaily);
        }

        // Processar top bots
        if (consolidatedData.topBots) {
          setTopBotClients(consolidatedData.topBots);
        }

        setCurrentMonthLoading(false);
        setHourlyComparisonMixedLoading(false);
        setLoading(false);

        // Dados principais carregados via endpoint otimizado
        // Buscar apenas dados adicionais não inclusos no consolidado
        // (top bots semanais, mensais, etc.)
        fetchAdditionalData();
        return;
      }
      // ============================================================
      // FALLBACK: Se endpoint consolidado falhar, usar método antigo
      // ============================================================
      console.warn('Endpoint consolidado falhou, usando fallback...');

      try {
        const hourlyComparisonMixedResponse = await api.getHourlyComparisonMixed();
        if (hourlyComparisonMixedResponse && Array.isArray(hourlyComparisonMixedResponse)) {
          setHourlyComparisonMixedData(hourlyComparisonMixedResponse);
        } else {
          console.warn('Hourly comparison mixed data não é um array ou está vazio:', hourlyComparisonMixedResponse);
          setHourlyComparisonMixedData([]);
        }
        
        // Buscar top botclients
        try {
          const topBotClientsResponse = await api.getTopBotClients();
          debug('Top botclients response:', topBotClientsResponse);
          
          if (topBotClientsResponse && Array.isArray(topBotClientsResponse)) {
            setTopBotClients(topBotClientsResponse);
          } else {
            console.warn('Top botclients data não é um array ou está vazio:', topBotClientsResponse);
            setTopBotClients([]);
          }
        } catch (error) {
          console.error('Erro ao carregar dados de top botclients:', error);
          setTopBotClients([]);
        }
      } catch (error) {
        console.error('Erro ao carregar dados de comparação horária mista:', error);
        setHourlyComparisonMixedData([]);
      }

      // Buscar top 50 bots da semana
      try {
        debug('Buscando top bots da semana...');
        const topWeeklyBotsResponse = await safeApiCall(
          api.getTopBotsWeekly()
        );
        
        debug('Resposta da API para top bots da semana:', topWeeklyBotsResponse);
        
        if (topWeeklyBotsResponse && topWeeklyBotsResponse.data) {
          setTopWeeklyBots(topWeeklyBotsResponse.data);
        } else if (topWeeklyBotsResponse) {
          // Se a resposta não tem a estrutura esperada mas existe, tenta usar diretamente
          debug('Usando dados diretamente da resposta:', topWeeklyBotsResponse);
          
          const dataArray = Array.isArray(topWeeklyBotsResponse) 
            ? topWeeklyBotsResponse 
            : [];
            
          setTopWeeklyBots(dataArray);
        }
      } catch (error) {
        console.error('Erro ao carregar dados de top bots da semana:', error);
        setTopWeeklyBots([]);
      }
      
      // Buscar interações por dia da semana
      try {
        debug('Buscando dados diários da semana...');
        const weeklyDailyDataResponse = await safeApiCall(
          api.getWeeklyDailyData()
        );
        
        debug('Resposta da API para dados diários da semana:', weeklyDailyDataResponse);
        
        if (weeklyDailyDataResponse && weeklyDailyDataResponse.data) {
          setWeeklyInteractionsPerDay(weeklyDailyDataResponse.data);
        } else if (weeklyDailyDataResponse) {
          // Se a resposta não tem a estrutura esperada mas existe, tenta usar diretamente
          debug('Usando dados diários diretamente da resposta:', weeklyDailyDataResponse);
          
          const dataArray = Array.isArray(weeklyDailyDataResponse) 
            ? weeklyDailyDataResponse 
            : [];
            
          setWeeklyInteractionsPerDay(dataArray);
        }
      } catch (error) {
        console.error('Erro ao carregar dados diários da semana:', error);
        setWeeklyInteractionsPerDay([]);
      }

      // Buscar top 50 bots do mês
      try {
        debug('Buscando top bots do mês...');
        const topMonthlyBotsResponse = await safeApiCall(
          api.getTopBotsMonthly()
        );
        
        debug('Resposta da API para top bots do mês:', topMonthlyBotsResponse);
        
        if (topMonthlyBotsResponse && topMonthlyBotsResponse.data) {
          setTopMonthlyBots(topMonthlyBotsResponse.data);
          
          // Calcular o total de interações mensais
          const totalMonthly = topMonthlyBotsResponse.data.reduce(
            (total: number, bot: any) => {
              const value = typeof bot.total_interactions === 'string' 
                ? parseInt(bot.total_interactions) 
                : (bot.total_interactions || 0);
              return total + value;
            },
            0
          );
          setTotalMonthlyInteractions(totalMonthly);
        } else if (topMonthlyBotsResponse) {
          // Se a resposta não tem a estrutura esperada mas existe, tenta usar diretamente
          debug('Usando dados diretamente da resposta:', topMonthlyBotsResponse);
          
          const dataArray = Array.isArray(topMonthlyBotsResponse) 
            ? topMonthlyBotsResponse 
            : [];
            
          setTopMonthlyBots(dataArray);
          
          // Calcular o total de interações mensais
          const totalMonthly = dataArray.reduce(
            (total: number, bot: any) => {
              const value = typeof bot.total_interactions === 'string' 
                ? parseInt(bot.total_interactions) 
                : (bot.total_interactions || 0);
              return total + value;
            },
            0
          );
          setTotalMonthlyInteractions(totalMonthly);
        }
      } catch (error) {
        console.error('Erro ao carregar dados de top bots do mês:', error);
        setTopMonthlyBots([]);
      }
      
      // Buscar interações por dia do mês
      try {
        debug('Buscando dados diários do mês...');
        const monthlyDailyDataResponse = await safeApiCall(
          api.getMonthlyDailyData()
        );
        
        debug('Resposta da API para dados diários do mês:', monthlyDailyDataResponse);
        debug('Estrutura da resposta:', JSON.stringify({
          hasData: !!monthlyDailyDataResponse,
          isObject: typeof monthlyDailyDataResponse === 'object',
          isArray: Array.isArray(monthlyDailyDataResponse),
          hasDataProperty: !!(monthlyDailyDataResponse && monthlyDailyDataResponse.data),
          dataPropertyIsArray: !!(monthlyDailyDataResponse && monthlyDailyDataResponse.data && Array.isArray(monthlyDailyDataResponse.data)),
          dataLength: monthlyDailyDataResponse && monthlyDailyDataResponse.data ? monthlyDailyDataResponse.data.length : 0,
          firstItemSample: monthlyDailyDataResponse && monthlyDailyDataResponse.data && monthlyDailyDataResponse.data.length > 0 
            ? JSON.stringify(monthlyDailyDataResponse.data[0]) 
            : 'No items'
        }, null, 2));
        
        if (monthlyDailyDataResponse && monthlyDailyDataResponse.data) {
          debug('Usando dados da propriedade data:', monthlyDailyDataResponse.data);
          setMonthlyInteractionsPerDay(monthlyDailyDataResponse.data);
        } else if (monthlyDailyDataResponse) {
          // Se a resposta não tem a estrutura esperada mas existe, tenta usar diretamente
          debug('Usando dados diários diretamente da resposta:', monthlyDailyDataResponse);
          
          const dataArray = Array.isArray(monthlyDailyDataResponse) 
            ? monthlyDailyDataResponse 
            : [];
            
          setMonthlyInteractionsPerDay(dataArray);
        }
      } catch (error) {
        console.error('Erro ao carregar dados diários do mês:', error);
        setMonthlyInteractionsPerDay([]);
      }

      // Buscar dados do mês corrente
      try {
        debug('Buscando dados do mês corrente...');
        setCurrentMonthLoading(true);
        
        const currentMonthResponse = await safeApiCall(
          api.getCurrentMonthTotalInteractions()
        );
        
        debug('Resposta da API para mês corrente:', currentMonthResponse);
        
        if (currentMonthResponse) {
          setCurrentMonthData(currentMonthResponse);
        }
      } catch (error) {
        console.error('Erro ao carregar dados do mês corrente:', error);
      } finally {
        setCurrentMonthLoading(false);
      }
    } catch (error) {
      console.error('🔴 ERRO CRÍTICO ao carregar dados do dashboard:', error);
      debug('🔴 Estado será resetado devido ao erro!');
      setError('Erro ao carregar dados do dashboard. Verifique o console para mais detalhes.');
      // Resetar estados para evitar exibir dados antigos em caso de erro
      setTotalInteractions(0);
      setHourlyData([]);
    } finally {
      debug('🟢 Finally block - Setting loading to false');
      debug('Estado final após fetch:', {
        totalInteractions,
        hourlyDataLength: hourlyData?.length,
        loading: false
      });
      setLoading(false);
      debug('=== FIM FETCH DASHBOARD DATA ===');
    }
  }, []);

  // Criar função auxiliar para chamadas de API seguras
  const safeApiCall = async (apiPromise: Promise<any>) => {
    try {
      debug('Making API call');
      return await apiPromise;
    } catch (error) {
      console.error('API call failed:', error);
      return null;
    }
  };
  
  // Calculate metrics
  const calculateMetrics = () => {
    // Find peak hour
    const peakHourData = [...hourlyData].sort((a, b) => b.hoje - a.hoje)[0];
    const peakHour = peakHourData ? peakHourData.hora : 0;
    const peakHourInteractions = peakHourData ? peakHourData.hoje : 0;
    
    // Calculate percentage differences
    let yesterdayDiff = 0;
    let lastWeekDiff = 0;

    if (yesterdayTotalInteractions > 0) {
      yesterdayDiff = ((totalInteractions - yesterdayTotalInteractions) / yesterdayTotalInteractions) * 100;
    }

    if (lastWeekTotalInteractions > 0) {
      lastWeekDiff = ((totalInteractions - lastWeekTotalInteractions) / lastWeekTotalInteractions) * 100;
    }
    
    // Calculate estimated revenue (0.39 por interação)
    const estimatedRevenue = (totalInteractions * 0.39).toFixed(2);
    
    return {
      peakHour,
      peakHourInteractions,
      yesterdayPercentageDiff: yesterdayDiff,
      lastWeekPercentageDiff: lastWeekDiff,
      estimatedRevenue,
      yesterdayDiff,
      lastWeekDiff
    };
  };
  
  // Format data for charts
  const formattedHourlyData = useMemo(
  () => formatHourlyData(hourlyData),
  [hourlyData]
);
  const formattedWeeklyData = useMemo(
  () => formatWeeklyData(weeklyData),
  [weeklyData]
);
  
  // Prepare data for pie chart
  const topBotsPie = [...topBots];
  const outrosTotal = totalInteractions - topBots.reduce((sum, item) => sum + item.total_interactions, 0);
  if (outrosTotal > 0) {
    topBotsPie.push({ bot_id: "Outros", bot_name: "Outros", total_interactions: outrosTotal });
  }
  
  // Get calculated metrics - Memoized for performance
  const metrics = useMemo(() => calculateMetrics(), [
    hourlyData,
    totalInteractions,
    yesterdayTotalInteractions,
    lastWeekTotalInteractions,
    hourlyComparisonMixedData
  ]);

  const {
    peakHour,
    peakHourInteractions,
    yesterdayPercentageDiff,
    lastWeekPercentageDiff,
    estimatedRevenue,
    yesterdayDiff,
    lastWeekDiff
  } = metrics;
  
  // Colors 
  const getBarColor = (value: number) => {
    if (value > 600) return '#00e396';
    if (value > 400) return '#4fcbff';
    if (value > 200) return '#8884d8';
    if (value > 100) return '#c3a7ff';
    return '#ff6b8a';
  };
  
  // Format current date
  const formatDate = () => {
    const now = new Date();
    return `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()}`;
  };
  
  // Format previous date for display
  const formatPreviousDate = (type: 'yesterday' | 'lastweek') => {
    const now = new Date();
    
    if (type === 'yesterday') {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return `${yesterday.getDate()}/${yesterday.getMonth() + 1}`;
    } else {
      const lastWeek = new Date(now);
      lastWeek.setDate(lastWeek.getDate() - 7);
      return `${lastWeek.getDate()}/${lastWeek.getMonth() + 1}`;
    }
  };
  
  // Format numbers into compact form (K, M, etc)
  const formatCompactNumber = (num: number) => {
    if (num === 0) return '0';
    
    const sign = Math.sign(num);
    const absNum = Math.abs(num);
    
    if (absNum < 1000) {
      return String(num);
    } else if (absNum < 1000000) {
      return (sign * (absNum / 1000)).toFixed(1) + 'K';
    } else if (absNum < 1000000000) {
      return (sign * (absNum / 1000000)).toFixed(1) + 'M';
    } else {
      return (sign * (absNum / 1000000000)).toFixed(1) + 'B';
    }
  };
  
  // Format the icon and color based on the percentage
  const formatPercentageDisplay = (percentage: number) => {
    const formattedPercentage = percentage.toFixed(2);
    const isPositive = percentage > 0;
    const isZero = percentage === 0;

    if (isZero) {
      return {
        icon: '⟷',
        color: 'text-gray-500',
        bgColor: 'bg-gray-500',
        value: formattedPercentage,
      };
    }

    return {
      icon: isPositive ? '↑' : '↓',
      color: isPositive ? 'text-green-500' : 'text-red-500',
      bgColor: isPositive ? 'bg-green-500' : 'bg-red-500',
      value: formattedPercentage,
    };
  };

  const yesterdayFormat = formatPercentageDisplay(yesterdayDiff);
  const lastWeekFormat = formatPercentageDisplay(lastWeekDiff);
  
  // Função para calcular a previsão de interações até o fim do dia
  const calculatePrediction = (): number => {
    // Se não tivermos dados suficientes ou for muito tarde, retornamos o valor atual
    if (currentHour <= 0 || currentHour >= 23) {
      return totalInteractions;
    }
    
    // Considerar apenas horas completas (ignorar a hora atual se não estiver completa)
    const completedHours = currentMinute < 15 ? currentHour - 1 : currentHour;
    
    if (completedHours <= 0) {
      return totalInteractions; // Se não temos pelo menos uma hora completa, retornar o total atual
    }
    
    // Se temos dados de ontem, usar a taxa de crescimento/perda para projeção
    if (yesterdayTotalInteractions > 0 && yesterdayTotalUntilNow > 0) {
      // Calcular a taxa de crescimento atual comparada com ontem até agora
      const growthRate = totalInteractions / yesterdayTotalUntilNow;
      
      // Projetar o resultado final usando a taxa de crescimento aplicada ao total de ontem
      const projectedTotal = Math.round(yesterdayTotalInteractions * growthRate);
      
      // Verificar se a projeção é razoável (pelo menos o valor atual)
      return Math.max(totalInteractions, projectedTotal);
    }
    
    // Fallback: se não temos dados de ontem, usar método de média simples
    // Taxa média de interações por hora completa até agora
    const hourlyRate = totalInteractions / completedHours;
    
    // Horas restantes no dia (incluindo a atual se não estiver completa)
    const remainingHours = 24 - completedHours;
    
    // Estimativa para o restante do dia
    const estimatedAdditional = hourlyRate * remainingHours;
    
    // Total previsto
    return Math.round(totalInteractions + estimatedAdditional);
  };
  
  // Função para encontrar o horário de pico
  const findPeakHour = (): string => {
    if (!hourlyData || hourlyData.length === 0) {
      return "--:--";
    }
    
    // Ordenar dados por número de interações (decrescente)
    const sortedHourlyData = [...hourlyData].sort((a, b) => {
      const interactionsA = a.hoje !== undefined ? a.hoje : (a.total_interacoes || 0);
      const interactionsB = b.hoje !== undefined ? b.hoje : (b.total_interacoes || 0);
      return interactionsB - interactionsA;
    });
    
    // Obter a hora com mais interações
    const peakHourData = sortedHourlyData[0];
    if (!peakHourData) return "--:--";
    
    const hour = peakHourData.hora !== undefined ? peakHourData.hora : 
               (peakHourData.hour !== undefined ? peakHourData.hour : 0);
    
    // Formatar hora no formato hh:00
    return `${String(hour).padStart(2, '0')}:00`;
  };
  
  // Função para obter o número de interações na hora de pico
  const findPeakHourInteractions = (): number => {
    if (!hourlyData || hourlyData.length === 0) {
      return 0;
    }
    
    // Ordenar dados por número de interações (decrescente)
    const sortedHourlyData = [...hourlyData].sort((a, b) => {
      const interactionsA = a.hoje !== undefined ? a.hoje : (a.total_interacoes || 0);
      const interactionsB = b.hoje !== undefined ? b.hoje : (b.total_interacoes || 0);
      return interactionsB - interactionsA;
    });
    
    // Obter as interações da hora de pico
    const peakHourData = sortedHourlyData[0];
    if (!peakHourData) return 0;
    
    return peakHourData.hoje !== undefined ? peakHourData.hoje : 
          (peakHourData.total_interacoes !== undefined ? peakHourData.total_interacoes : 0);
  };
  
  // Função para calcular a taxa de crescimento por hora
  const calculateGrowthRate = (): number => {
    // Se não temos dados suficientes, retornamos zero ou a média simples
    if (currentHour <= 0) {
      return 0;
    }
    
    // Para uma estimativa simples, dividimos o total pelo número de horas decorridas
    const simpleRate = Math.round(totalInteractions / currentHour);
    
    // Se temos dados horários mais detalhados, podemos calcular uma taxa mais precisa
    if (hourlyData && hourlyData.length >= 3) {
      // Pegar as últimas 3 horas
      const recentHours = [...hourlyData]
        .filter(item => {
          const hour = item.hora !== undefined ? item.hora : 
                      (item.hour !== undefined ? item.hour : 0);
          return hour <= currentHour && hour >= currentHour - 3;
        })
        .sort((a, b) => {
          const hourA = a.hora !== undefined ? a.hora : (a.hour !== undefined ? a.hour : 0);
          const hourB = b.hora !== undefined ? b.hora : (b.hour !== undefined ? b.hour : 0);
          return hourA - hourB;
        });
        
      if (recentHours.length >= 2) {
        // Calcular a média de crescimento das últimas horas
        let totalGrowth = 0;
        for (let i = 1; i < recentHours.length; i++) {
          const prevInteractions = recentHours[i-1].hoje !== undefined ? recentHours[i-1].hoje : 
                                  (recentHours[i-1].total_interacoes || 0);
          const currentInteractions = recentHours[i].hoje !== undefined ? recentHours[i].hoje : 
                                    (recentHours[i].total_interacoes || 0);
          
          totalGrowth += Math.max(0, currentInteractions - prevInteractions);
        }
        
        const averageGrowth = Math.round(totalGrowth / (recentHours.length - 1));
        return averageGrowth > 0 ? averageGrowth : simpleRate;
      }
    }
    
    return simpleRate;
  };
  
  // Função para calcular a relação entre o pico e a média
  const calculatePeakToAverageRatio = (): number => {
    if (!hourlyData || hourlyData.length === 0 || currentHour <= 0) {
      return 0;
    }
    
    // Encontrar o valor de pico
    const peakInteractions = findPeakHourInteractions();
    
    // Calcular a média de interações por hora (excluindo horas sem dados)
    let totalInteractionsInHours = 0;
    let hoursWithData = 0;
    
    hourlyData.forEach(item => {
      const interactions = item.hoje !== undefined ? item.hoje : 
                         (item.total_interacoes || 0);
      
      if (interactions > 0) {
        totalInteractionsInHours += interactions;
        hoursWithData++;
      }
    });
    
    const averagePerHour = hoursWithData > 0 ? totalInteractionsInHours / hoursWithData : 0;
    
    // Evitar divisão por zero
    if (averagePerHour <= 0) {
      return 0;
    }
    
    // Calcular a porcentagem do pico em relação à média
    const ratio = (peakInteractions / averagePerHour - 1) * 100;
    
    // Retorna a porcentagem com no máximo um decimal
    return Math.round(ratio * 10) / 10;
  };
  
  // Função para formatar o eixo X por minuto
  const formatMinuteXAxis = (value: number) => {
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };
  
  // Função para mudar a visualização dos minutos (ranges predefinidos)
  const handleMinuteRangeChange = (range: string) => {
    let start = 0;
    let end = 1439;
    
    switch(range) {
      case '2h':
        start = 0;
        end = 119;
        break;
      case '6h':
        start = 0;
        end = 359;
        break;
      case '12h':
        start = 0;
        end = 719;
        break;
      case 'business':
        start = 8 * 60; // 8:00
        end = 18 * 60 - 1; // 17:59
        break;
      case 'evening':
        start = 18 * 60; // 18:00
        end = 23 * 60 + 59; // 23:59
        break;
      case 'night':
        start = 0;
        end = 8 * 60 - 1; // 7:59
        break;
      default:
        break;
    }
    
    setMinuteViewRange([start, end]);
    setMinuteDataFiltered(minuteData.filter(item => item.minuto >= start && item.minuto <= end));
  };
  
  // Atualizar o processamento de dados de minuto para criar aggregated10MinData
  const processMinuteData = (minuteData: MinuteDataItem[]) => {
    if (!minuteData || minuteData.length === 0) {
      setAggregated10MinData([]);
      return;
    }

    // Agrupar por intervalo de 10 minutos
    const groupedData: Record<string, Aggregated10MinData> = {};
    
    minuteData.forEach(item => {
      // Calcular o intervalo de 10 minutos (0-9, 10-19, 20-29, etc.)
      const minute = item.minuto;
      const interval = Math.floor(minute / 10) * 10;
      const intervalKey = `${Math.floor(interval / 60)}:${String(interval % 60).padStart(2, '0')}`;
      
      if (!groupedData[intervalKey]) {
        groupedData[intervalKey] = {
          interval: intervalKey,
          hoje: 0,
          ontem: 0,
          semana_anterior: 0
        };
      }
      
      groupedData[intervalKey].hoje += item.interacoes_hoje;
      groupedData[intervalKey].ontem += item.interacoes_ontem;
      groupedData[intervalKey].semana_anterior += item.interacoes_semana_anterior;
    });
    
    // Converter para array e ordenar
    const result = Object.values(groupedData).sort((a, b) => {
      const [hoursA, minsA] = a.interval.split(':').map(Number);
      const [hoursB, minsB] = b.interval.split(':').map(Number);
      
      if (hoursA !== hoursB) {
        return hoursA - hoursB;
      }
      return minsA - minsB;
    });
    
    setAggregated10MinData(result);
  };
  
  // Formatar dados mensais para exibição
  const formattedMonthlyData = useMemo(() => {
  if (!monthlyData || monthlyData.length === 0) return [];
    return monthlyData.map(item => ({
      month: new Date(item.month).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }),
      total: parseInt(item.total_interactions)
    }));
  }, [monthlyData]);

  // Formatar dados diários para exibição
  const formattedDailyData = useMemo(() => {
  if (!dailyData || dailyData.length === 0) return [];
    return dailyData.map(item => ({
      day: new Date(item.day).toLocaleDateString('pt-BR', { day: '2-digit' }),
      total: parseInt(item.total_interactions)
    }));
  }, [dailyData]);

  // Componente para visualizar top bots
  const renderTopBotsChart = () => {
    if (!topBots || topBots.length === 0) {
      return (
        <div className="no-data">
          <p>Sem dados de bots disponíveis</p>
        </div>
      );
    }
    
    return (
      <div className="top-bots-chart card">
        <h3>Top 30 Bots por Interações (Mês Atual)</h3>
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={topBots}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis type="number" tick={{ fill: '#bbb' }} />
            <YAxis 
              type="category" 
              dataKey="bot_name" 
              width={120}
              tick={{ fontSize: 12, fill: '#bbb' }}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#333', border: 'none' }}
              formatter={(value) => [`${value.toLocaleString()} interações`, 'Total']}
            />
            <Legend />
            <Bar dataKey="total_interactions" fill="#8884d8" name="Interações" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Componente de gráfico mensal
  const renderMonthlyChart = () => {
    if (!formattedMonthlyData || formattedMonthlyData.length === 0) {
      return (
        <div className="no-data">
          <p>Sem dados mensais disponíveis</p>
        </div>
      );
    }
    
    return (
      <div className="monthly-chart card">
        <h3>Interações por Mês (Últimos 12 meses)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={formattedMonthlyData}
            margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis
              dataKey="month"
              tick={{ fill: '#bbb', fontSize: 12 }}
              angle={-45}
              textAnchor="end"
            />
            <YAxis tick={{ fill: '#bbb' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#333', border: 'none' }}
              formatter={(value) => [`${value.toLocaleString()} interações`, 'Total']}
            />
            <Legend />
            <Bar dataKey="total" fill="#8884d8" name="Interações" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Componente de gráfico diário do mês atual
  const renderDailyMonthChart = () => {
    if (!formattedDailyData || formattedDailyData.length === 0) {
      return (
        <div className="no-data">
          <p>Sem dados diários disponíveis</p>
        </div>
      );
    }
    
    return (
      <div className="daily-month-chart card">
        <h3>Interações Diárias (Mês Atual)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart
            data={formattedDailyData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis dataKey="day" tick={{ fill: '#bbb' }} />
            <YAxis tick={{ fill: '#bbb' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#333', border: 'none' }}
              formatter={(value) => [`${value.toLocaleString()} interações`, 'Total']}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="total" 
              stroke="#8884d8" 
              name="Interações" 
              strokeWidth={2} 
              dot={{ r: 4 }} 
              activeDot={{ r: 6 }} 
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Componente de gráfico para dados agrupados em intervalos de 10 minutos
  const render10MinIntervalChart = () => {
    if (!aggregated10MinData || aggregated10MinData.length === 0) {
      return (
        <div className="no-data">
          <p>Sem dados de intervalos de 10 minutos disponíveis</p>
        </div>
      );
    }
    
    return (
      <div className="minute-interval-chart card">
        <h3>Interações por Intervalos de 10 Minutos</h3>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart
            data={aggregated10MinData}
            margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis
              dataKey="interval"
              tick={{ fill: '#bbb', fontSize: 12 }}
              interval={Math.floor(aggregated10MinData.length / 20)}
              angle={-45}
              textAnchor="end"
            />
            <YAxis tick={{ fill: '#bbb' }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#333', border: 'none' }}
              formatter={(value) => [`${value.toLocaleString()} interações`, '']}
            />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="hoje" 
              name="Hoje" 
              fill="#8884d8" 
              stroke="#8884d8"
              fillOpacity={0.2}
              strokeWidth={2}
            />
            <Line 
              type="monotone" 
              dataKey="ontem" 
              name="Ontem" 
              stroke="#82ca9d" 
              dot={false}
              strokeWidth={1.5}
            />
            <Line 
              type="monotone" 
              dataKey="semana_anterior" 
              name="Semana Anterior" 
              stroke="#ff7300" 
              dot={false}
              strokeWidth={1.5}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Corrigir a função calculatePercentages
  const calculatePercentages = () => {
    debug('Calculating percentages with:', {
      total: totalInteractions,
      yesterday: yesterdayTotalInteractions,
      lastWeek: lastWeekTotalInteractions
    });

    // Calcular percentagem em relação a ontem
    if (yesterdayTotalInteractions > 0 && totalInteractions > 0) {
      const percentage = ((totalInteractions - yesterdayTotalInteractions) / yesterdayTotalInteractions) * 100;
      setYesterdayPercentage(percentage);
    } else {
      setYesterdayPercentage(0);
    }

    // Calcular percentagem em relação à semana passada
    if (lastWeekTotalInteractions > 0 && totalInteractions > 0) {
      const percentage = ((totalInteractions - lastWeekTotalInteractions) / lastWeekTotalInteractions) * 100;
      setLastWeekPercentage(percentage);
    } else {
      setLastWeekPercentage(0);
    }
  };

  // Função para verificar se os dados são do dia atual
  const checkCurrentData = (data: any) => {
    if (!data) return;
    
    const now = new Date();
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Verifica se o objeto de dados tem uma propriedade 'date'
    if (data.date) {
      const dataDate = new Date(data.date);
      const isCurrentDay = isSameDay(dataDate, currentDate);
      
      if (!isCurrentDay) {
        setIsShowingCurrentData(false);
        setMostRecentDate(dataDate);
      }
      return;
    }
    
    // Para dados de séries temporais, verifica se há registros para a data atual
    if (Array.isArray(data)) {
      // Dados semanais - verifica se a data mais recente é hoje
      const hasCurrentDate = data.some(item => {
        if (item.data) {
          const itemDate = new Date(item.data);
          return isSameDay(itemDate, currentDate);
        }
        return false;
      });
      
      if (!hasCurrentDate && data.length > 0) {
        // Encontrar a data mais recente
        let latestDate: Date | null = null;
        
        data.forEach(item => {
          if (item.data) {
            const itemDate = new Date(item.data);
            if (!latestDate || itemDate > latestDate) {
              latestDate = itemDate;
            }
          }
        });
        
        if (latestDate) {
          setIsShowingCurrentData(false);
          setMostRecentDate(latestDate);
        }
      }
    }
  };

  // Calcular estatísticas dos dados

  if (loading) {
    debug('🟠 RENDER: Loading state - showing spinner');
    debug('Current data state:', {
      totalInteractions,
      hourlyDataLength: hourlyData?.length
    });
    
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-400 border-r-transparent"></div>
          <p className="mt-4">Carregando dados do dashboard...</p>
        </div>
      </div>
    );
  }
  
  debug('🟢 RENDER: Main dashboard - data loaded');
  debug('Rendering with data:', {
    totalInteractions,
    hourlyDataLength: hourlyData?.length,
    loading,
    error
  });
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 text-gray-100 p-4">
      {/* Confetti effect */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(50)].map((_, i) => (
            <div 
              key={i} 
              className="confetti" 
              style={{
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                backgroundColor: [
                  '#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', 
                  '#fcff42', '#ffa62d', '#ff36ff'
                ][Math.floor(Math.random() * 7)],
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${Math.random() * 2 + 3}s`
              }}
            />
          ))}
        </div>
      )}
      
      {/* Header */}
      <DashboardHeader
        title="Performance dos Funcionários Digitais"
        lastUpdated={new Date().toLocaleString('pt-BR')}
        onRefresh={() => {
          setRefreshing(true);
          fetchDashboardData().finally(() => {
            setRefreshing(false);
          });
        }}
        refreshing={refreshing}
        goalTriggered={goalTriggered}
        currentEmojis={currentEmojis}
      />

      <div className="flex items-center mb-6">
        {!isShowingCurrentData && mostRecentDate && (
          <div className="bg-yellow-700/30 border border-yellow-600/50 text-yellow-200 px-3 py-1 rounded-md text-sm">
            <span className="font-semibold">Atenção:</span> Mostrando dados de {mostRecentDate.toLocaleDateString('pt-BR')}
          </div>
        )}
      </div>

      {/* Debug Info */}
      {error && (
        <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg text-red-100 animate-pulse">
          <p className="font-semibold">Erro:</p>
          <p>{error}</p>
        </div>
      )}

      {/* Card de Destaque do Mês Corrente */}
      <div className="mb-8">
        <MonthlyHighlightCard 
          totalInteractions={currentMonthData.total_interacoes}
          monetaryValue={currentMonthData.valor_monetario}
          isLoading={currentMonthLoading}
          monthStart={currentMonthData.month_start}
          monthEnd={currentMonthData.month_end}
        />
      </div>

      {/* Linha 1: Cards Principais */}
      <div className="metrics-container">
        <MetricCard
          title="Total de Hoje"
          value={Math.round(totalInteractions * 0.39 * 100) / 100}
          format="currency"
          subtitle="Total de interações"
          subValue={totalInteractions}
          description={`R$ 0,39 por interação`}
          variant="primary"
          icon={<BarChartIcon />}
          animated={true}
          className="total-hoje-card"
        />
        
        {/* Log detalhado para depuração do Delta Diário */}
        {(() => {
          debug('DEBUG - Delta Diário:', {
            todayTotalUntilNow,
            yesterdayTotalUntilNow,
            diferenca: todayTotalUntilNow - yesterdayTotalUntilNow,
            percentual: yesterdayPercentage,
            horaAtual: currentHour,
            horaComparacao: currentHour-1
          });
          return null;
        })()}
        
        <MetricCard
          title="Δ Diário"
          value={todayTotalUntilNow - yesterdayTotalUntilNow}
          format="number"
          change={yesterdayPercentage}
          description={`Comparado até ${currentHour}:00h`}
          variant={yesterdayPercentage >= 0 ? "success" : "danger"}
          icon={<TrendUpIcon />}
          animated={true}
          pulse={Math.abs(yesterdayPercentage) > 15}
        />

        <MetricCard
          title="Δ Semanal"
          value={todayTotalUntilNow - lastWeekTotalUntilNow}
          format="number"
          change={lastWeekPercentage}
          description={`Comparado até ${currentHour}:00h`}
          variant={lastWeekPercentage >= 0 ? "success" : "danger"}
          icon={<LineChartIcon />}
          animated={true}
          pulse={Math.abs(lastWeekPercentage) > 15}
        />

        <MetricCard
          title="Média por Hora"
          value={totalInteractions > 0 && currentHour > 0 ? Math.round(totalInteractions / currentHour) : 0}
          format="number"
          description={`Baseado em ${currentHour} horas completas`}
          variant="purple"
          icon={<ClockIcon />}
          animated={true}
        />
      </div>

      {/* Nova Linha: Cards de Insights */}
      <div className="mb-5">
        <h3 className="text-lg font-medium text-gray-200 mb-2 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Insights Automatizados
        </h3>
        <div className="metrics-container insights-container">
          {/* Card de Previsão */}
          <MetricCard
            title="Projeção para Hoje"
            value={calculatePrediction()}
            format="number"
            description={`Estimativa com base nas últimas ${currentHour}h`}
            variant="primary"
            icon={<CalendarIcon />}
            animated={true}
            pulse={calculatePrediction() / totalInteractions > 1.5}
          />
          
          {/* Card de Horário de Pico */}
          <MetricCard
            title="Horário de Pico"
            value={findPeakHour()}
            format="string"
            description={`${findPeakHourInteractions().toLocaleString()} interações neste período`}
            variant="warning"
            icon={<FireIcon />}
            pulse={findPeakHourInteractions() > 1000}
          />
          
          {/* Card de Taxa de Crescimento */}
          <MetricCard
            title="Crescimento/Hora"
            value={calculateGrowthRate()}
            format="number"
            description={`Taxa média com base nas últimas horas`}
            variant="success"
            icon={<BoltIcon />}
            animated={true}
            pulse={calculateGrowthRate() > 200}
          />
          
          {/* Card de Total da Semana (substituindo o Pico vs Média) */}
          <MetricCard
            title="Total da Semana"
            value={totalWeeklyInteractions * 0.39}
            format="currency"
            subtitle="Total de interações"
            subValue={totalWeeklyInteractions}
            description={`R$ 0,39 por interação`}
            variant="purple"
            icon={<CalendarIcon />}
            animated={true}
            className="smaller-value"
          />
        </div>
      </div>

      {/* Tabs para diferentes visualizações */}
      <TabNavigation 
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as any)}
        tabs={[
          { id: 'hourly', label: 'Análise Diária' },
          { id: 'weekly', label: 'Análise Semanal' },
          { id: 'monthly', label: 'Análise Mensal' },
          { id: 'evolution', label: 'Partenon' }
        ]}
      />

      {/* Conteúdo da Tab Atual */}
      <div className="tab-content">
        {activeTab === 'hourly' && (
          <>
            <div className="chart-row">
              <HourlyMixedComparisonChart 
                data={hourlyComparisonMixedData}
                loading={loading}
              />
            </div>
            
            <InteractionRangeAnalysis 
              data={topBotClients}
              timeFrame="daily"
              loading={loading}
            />
          </>
        )}
        
        {activeTab === 'monthly' && (
          <>
            <div className="chart-row">
              <DailyInteractionsChart 
                data={monthlyInteractionsPerDay}
                loading={loading}
                mode="monthly"
                title="Interações Diárias do Mês"
              />
            </div>
            
            <div className="mt-6">
              <InteractionRangeAnalysis
                data={topMonthlyBots}
                timeFrame="monthly"
                loading={loading}
              />
            </div>
          </>
        )}
        
        {activeTab === 'weekly' && (
          <>
            <div className="chart-row">
              <WeeklyMixedComparisonChart 
                data={weeklyInteractionsPerDay}
                loading={loading}
              />
            </div>
            
            <div className="mt-6">
              <InteractionRangeAnalysis
                data={topWeeklyBots}
                timeFrame="weekly"
                loading={loading}
              />
            </div>
          </>
        )}
        {activeTab === 'evolution' && <PartenonTab />}
      </div>

      {/* Painel de Top Bots por Interações */}
      <div className="mb-5 mt-8">
        <h3 className="text-lg font-medium text-gray-200 mb-2 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          Ranking de Bots por Interações
        </h3>
        <TopBotsRankingPanel />
      </div>

      {/* Painel de Alertas de Bots - movido para o final */}
      <div className="mb-5 mt-8">
        <h3 className="text-lg font-medium text-gray-200 mb-2 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Monitoramento de Bots Inativos
        </h3>
        <BotAlertsPanel />
      </div>

      {/* Ranking de FDs por interações */}
      <FDRankingPanel />

      {/* Footer */}
      <div className="mt-6 flex justify-between items-center text-gray-500 text-sm border-t border-gray-800 pt-4">
        <div className="flex items-center">
          <ClockIcon className="h-4 w-4 mr-1" />
          <span>Última atualização: {new Date().toLocaleString('pt-BR')}</span>
        </div>
        <div className="flex items-center">
          <span>Total de interações: {totalInteractions.toLocaleString()} (R$ {(totalInteractions * 0.39).toFixed(2)})</span>
          <div className="ml-2 h-2 w-2 rounded-full bg-green-400 animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default DashboardIntegrado; 
